import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { PulsingLoader } from "@/components/ui/pulsing-loader";

const spinnerVariants = cva(
  "animate-spin rounded-full border-2 border-current border-t-transparent",
  {
    variants: {
      size: {
        sm: "h-4 w-4 border-2",
        md: "h-8 w-8 border-2",
        lg: "h-12 w-12 border-3",
        xl: "h-16 w-16 border-4",
      },
      variant: {
        default: "text-primary",
        secondary: "text-muted-foreground",
        success: "text-green-600",
        destructive: "text-destructive",
        logo: "", // Special variant for logo spinner
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

export interface LoadingSpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  label?: string;
}

/**
 * Atom: Loading spinner component with size variants
 * Shows a spinning circle animation or pulsing loader to indicate loading state
 * @deprecated For new code, prefer using PulsingLoader from @/components/ui/pulsing-loader
 */
export function LoadingSpinner({
  size,
  variant,
  label,
  className,
  ...props
}: LoadingSpinnerProps) {
  // Use pulsing loader for better UX
  if (variant === "logo" || size === "lg" || size === "xl") {
    return (
      <PulsingLoader
        size={size === "sm" ? "sm" : size === "lg" ? "lg" : size === "xl" ? "xl" : "md"}
        label={label}
        showLabel={!!label}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn("flex items-center justify-center gap-2", className)}
      role="status"
      aria-live="polite"
      {...props}
    >
      <div className={cn(spinnerVariants({ size, variant }))} aria-hidden="true" />
      {label && <span className="text-sm text-muted-foreground">{label}</span>}
      <span className="sr-only">{label || "Cargando..."}</span>
    </div>
  );
}
